export class Zoomer {
  constructor(options) {
    this.zoomDelta = options.zoomDelta;
    this.zoomInterval = options.zoomInterval;
    // The container of the zoomer element. The container is there to add margins, etc.
    this.targetContainer = options.targetContainer;
    this.zoomOutBtn = options.zoomOutBtn;
    this.zoomInBtn = options.zoomInBtn;
    this.containBtn = options.containBtn;
    this.pct100Btn = options.pct100Btn;
    this.fullWidthBtn = options.fullWidthBtn;
    this.fullHeightBtn = options.fullHeightBtn;
    this.fullscreenBtn = options.fullscreenBtn;
    this.closeBtn = options.closeBtn;
    this.isUsingTransitions = options.isUsingTransitions;
    this.autoActivate = options.autoActivate;
    this.sizerStrategy = options.sizerStrategy;
    this.onDeactivate = options.onDeactivate;
    this.onActivated = options.onActivated;

    this.AspectRatioSizer = options.AspectRatioSizerClass;
    this.RepeatWhileMouseDown = options.RepeatWhileMouseDownClass;
    this.DragToScroll = options.DragToScrollClass;
    this.Utils = options.UtilsClass;

    /* This element must be the first child of the targetContainer element, and will become the
     * parent of the targetElement element. */
    this.targetParent = this.targetContainer.firstElementChild;
    this.assignTargetElement(options.targetElement, this.autoActivate);

    this.containBtn.addEventListener('click', () => this.containImage());
    this.pct100Btn.addEventListener('click', () => this.zoomTo100Pct());
    this.fullWidthBtn.addEventListener('click', () => this.zoomToFullWidth());
    this.fullHeightBtn.addEventListener('click', () => this.zoomToFullHeight());
    if (this.fullscreenBtn) {
      this.fullscreenBtn.addEventListener('click', () => this.activateFullscreen());
    }
    this.closeBtn.addEventListener('click', () => this.deactivateFullscreen());
    this.targetParent.addEventListener('mousedown', event => {
      this.origMouseX = event.clientX;
      this.origMouseY = event.clientY;
    });
    this.targetParent.addEventListener('click', event => {
      if (event.target === this.targetParent) {
        const dx = event.clientX - this.origMouseX;
        const dy = event.clientY - this.origMouseY;
        // Prevent drags from being considered clicks:
        if (Math.abs(dx) < 20 && Math.abs(dy) < 20) {
          this.deactivateFullscreen();
        }
      }
    });

    this.zoomOutRepeat = new this.RepeatWhileMouseDown(this.zoomOutBtn, this.zoomInterval,
      () => this.prepareRepeatedZoom(false),
      () => this.decreaseZoom(),
      wasClick => this.handleZoomRepeatDone(wasClick, false));
    this.zoomInRepeat = new this.RepeatWhileMouseDown(this.zoomInBtn, this.zoomInterval,
      () => this.prepareRepeatedZoom(true),
      () => this.increaseZoom(),
      wasClick => this.handleZoomRepeatDone(wasClick, true));

    /* dragToScroll enables pointer devices to scroll the product image by using a drag operation.
     * On touch devices this functionality is built-in. */
    this.dragToScroll = new this.DragToScroll(this.targetParent, this.targetElement);

    this.sizer = new this.AspectRatioSizer(options.sizerStrategy, true);
    this.lastZoom = () => {} // Noop by default
  }

  updateToolbarButtons() {
    if (this.AspectRatioSizer.hasHigherWHRatioThanParent(this.targetElement, this.targetWidthHeightRatio)) {
      this.fullWidthBtn.style.display = 'none';
      this.fullHeightBtn.style.display = '';
    } else {
      this.fullWidthBtn.style.display = '';
      this.fullHeightBtn.style.display = 'none';
    }
  }

  assignTargetElement(targetElement, autoActivate) {
    if (targetElement !== this.targetElement) {
      if (this.targetElement) {
        // Clean up
        this.Utils.removeEventListener(this.imageLoadListener);
        this.Utils.removeEventListener(this.windowLoadListener);
        if (this.isActive) {
          this.deactivateFullscreen();
        }
      }
      this.targetElement = targetElement;
      if (this.targetElement) {
        this.dragToScroll.updateChildElement(this.targetElement);
        // The element we are zooming:
        this.hasNaturalSize = typeof this.targetElement.naturalWidth != 'undefined';
        if (autoActivate) {
          if (this.targetElement.tagName.toLowerCase() === 'img') {
            if (this.targetElement.complete) {
              this.activateFullscreen();
            } else {
              this.imageLoadListener = this.Utils.addEventListener(this.targetElement, 'load', () => {
                this.activateFullScreen();
                this.Utils.removeEventListener(this.imageLoadListener);
              });
            }
          } else {
            if (document.readyState === 'complete') {
              this.activateFullscreen();
            } else {
              this.windowLoadListener = this.Utils.addEventListener(window, 'load', () => {
                this.activateFullscreen();
                this.Utils.removeEventListener(this.windowLoadListener);
              });
            }
          }
        }
      }
    }
  }

  activateFullscreen() {
    this.targetOriginalStyle = this.targetElement.getAttribute('style');
    this.targetOriginalParent = this.targetElement.parentNode;
    this.targetOriginalSibling = this.targetElement.nextSibling;
    this.targetContainer.classList.remove('hidden');
    if (!this.hasNaturalSize) {
      this.pct100BtnDisplayStyle = this.pct100Btn.style.display;
      this.pct100Btn.style.display = 'none';
    }
    const docStyle = document.documentElement.style;
    this.origDocOverflow = docStyle.overflow;
    docStyle.overflow = 'hidden';
    this.transitionstartListener = this.Utils.addEventListener(this.targetElement, 'transitionstart', () => this.handleTransitionStart());
    this.transitionendListener = this.Utils.addEventListener(this.targetElement, 'transitionend', () => this.handleTransitionEnd());
    this.resizeListener = this.Utils.addEventListener(window, 'resize', () => {
      this.lastZoom();
      this.updateToolbarButtons();
    });
    this.dragToScroll.start();
    this.isActive = true;

    // We have to add a short delay after changing visibility in order for transition effects to work.
    setTimeout(() => {
      // Capture the target element's natural size. We assume there are no width/height set on it at this point
      this.targetWidth = this.targetElement.clientWidth;
      this.targetHeight = this.targetElement.clientHeight;
      this.targetWidthHeightRatio = this.AspectRatioSizer.getWidthHeightRatio(this.targetElement);

      this.targetParent.insertBefore(this.targetElement, this.targetParent.firstElementChild);
      this.targetContainer.classList.add('active');

      this.updateToolbarButtons();
      this.containImage(false);
      this.keydownListener = this.Utils.addEventListener(document, 'keydown', () => this.deactivateFullscreen());
      if (this.onActivated) {
        this.onActivated();
      }
    }, 5);
  }

  deactivateFullscreen() {
    this.Utils.removeEventListener(this.keydownListener);
    this.Utils.removeEventListener(this.resizeListener);
    this.Utils.removeEventListener(this.transitionstartListener);
    this.Utils.removeEventListener(this.transitionendListener);
    this.dragToScroll.stop();

    if (!this.hasNaturalSize) {
      this.pct100Btn.style.display = this.pct100BtnDisplayStyle;
    }
    if (this.targetOriginalStyle) {
      this.targetElement.setAttribute('style', this.targetOriginalStyle);
    } else {
      this.targetElement.removeAttribute('style');
    }
    document.documentElement.style.overflow = this.origDocOverflow;
    this.disableTransitions();
    this.targetOriginalParent.insertBefore(this.targetElement, this.targetOriginalSibling);
    this.isActive = false;

    // Allow the target element to be put back into place before hiding the zoomer:
    setTimeout(() => {
      this.targetContainer.classList.remove('active');
      // We need to wait at least for the duration of the css transition before changing visibility:
      setTimeout(() => {
        this.targetContainer.classList.add('hidden');
        if (this.onDeactivate) {
          this.onDeactivate();
        }
      }, 500);
    }, 10);
  }

  getZoomValueFromStyle() {
    const css = this.targetElement.style.width;
    if (css === 'auto') {
      return NaN;
    }
    const str = css.substring(0, css.length - 1);
    return Number(str);
  }

  setStyleFromCurrentZoom(height) {
    const style = this.targetElement.style;
    style.width = isNaN(this.currentZoom) ? 'auto' : this.currentZoom + '%';
    if (!this.hasNaturalSize) {
      // Must also update height whenever we change width:
      style.height = height + 'px';
    }
  }

  convertAutoZoomToPct() {
    if (isNaN(this.currentZoom)) {
      this.currentZoom = 100 * this.targetElement.clientWidth / this.targetElement.parentNode.clientWidth;
    }
  }

  getWidthAsPercent() {
    return 100 * this.targetWidth / this.targetElement.parentNode.clientWidth;
  }

  handleTransitionStart() {
    const style = this.targetElement.style;
    if (style.width === 'auto') {
      style.width = this.getWidthAsPercent() + '%';
    }
  }

  handleTransitionEnd() {
    if (this.setWidthToAutoAtEnd) {
      this.targetElement.style.width = 'auto';
      this.setWidthToAutoAtEnd = false;
    }
  }

  disableTransitions() {
    this.targetElement.classList.remove('transition');
  }

  enableTransitions() {
    this.targetElement.classList.add('transition');
  }

  applyCurrentZoom() {
    this.setStyleFromCurrentZoom(this.sizer.childHeight);
    this.sizer.alignCenter(this.targetElement, true);
  }

  decreaseZoom(multiplier = 1) {
    this.currentZoom = this.sizer.changeZoom(this.targetElement, this.currentZoom, -this.zoomDelta * multiplier);
    this.applyCurrentZoom();
  }

  increaseZoom(multiplier = 1) {
    this.currentZoom = this.sizer.changeZoom(this.targetElement, this.currentZoom, this.zoomDelta * multiplier);
    this.applyCurrentZoom();
  }

  prepareRepeatedZoom(isZoomIn) {
    this.disableTransitions();
    this.convertAutoZoomToPct();
    this.lastZoom = () => {
      this.currentZoom = this.sizer.changeZoom(this.targetElement, this.currentZoom, 0);
      this.applyCurrentZoom();
    };
  }

  handleZoomRepeatDone(wasClick, isZoomIn) {
    this.currentZoom = this.getZoomValueFromStyle();
    if (wasClick) {
      this.disableTransitions();
      if (isZoomIn) {
        this.increaseZoom(4);
      } else {
        this.decreaseZoom(4);
      }
    }
  }

  zoomTo100Pct() {
    const style = this.targetElement.style;
    if (this.isUsingTransitions) {
      this.setWidthToAutoAtEnd = false;
      this.enableTransitions();
      const width = this.getWidthAsPercent();
      style.width = width + '%';
    } else {
      style.width = 'auto';
    }
    this.currentZoom = this.getZoomValueFromStyle();
    style.height = 'auto';
    this.sizer.childWidth = this.targetWidth;
    this.sizer.childHeight = this.targetHeight;
    style.left = 0;
    style.top = 0;
    //this.sizer.alignCenter(this.targetElement, true);
    this.lastZoom = () => this.zoomTo100Pct();
  }

  containImage(enableTransitions = true) {
    if (!this.targetElement.parentNode) {
      // We have a case there the targetElement doesn't have a parent. Not sure why yet. 11/22/2021.
      return;
    }
    if (enableTransitions) {
      this.enableTransitions();
    }
    this.sizer.contain(this.targetElement, this.targetWidthHeightRatio);
    this.sizer.alignCenter(this.targetElement, true);
    this.currentZoom = this.getZoomValueFromStyle();
    this.lastZoom = () => this.containImage();
  }

  zoomToFullWidth() {
    this.enableTransitions();
    this.sizer.sizeToFullWidth(this.targetElement);
    this.sizer.alignCenter(this.targetElement, true);
    this.currentZoom = this.getZoomValueFromStyle();
    this.lastZoom = () => this.zoomToFullWidth();
  }

  zoomToFullHeight() {
    this.enableTransitions();
    this.sizer.sizeToFullHeight(this.targetElement);
    this.sizer.alignCenter(this.targetElement, true);
    this.currentZoom = this.getZoomValueFromStyle();
    this.lastZoom = () => this.zoomToFullHeight();
  }
}